/**
 * Shared hook: load capability Provider Options once per workspace+capability
 * (via canvasSharedConfigLoaders single-flight cache). No per-node polling.
 * Passes current i18n language as `locale` so Admin presentation SoT localizes.
 * Window focus invalidates catalog cache so Admin enable/default changes apply
 * without redeploy (reload or refocus is enough).
 *
 * VistoeraTASK-00411 — a request that fails during a transient backend
 * blip (e.g. a Render Pre-Deploy restart window) previously left `error`
 * stuck true forever for any node instance that happened to mount/fetch
 * during that exact window, until the user did something that already
 * triggers the `focus` listener below (blur+refocus) or a hard reload —
 * surfacing as "Provider 加载失败" even seconds after the real backend
 * had already recovered. Bounded, backoff auto-retry (same single-flight
 * cache, zero new endpoints, never fabricates a Provider) closes that gap
 * without turning this into an indefinite background-polling loop.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import type { WorkspaceProviderOption } from '../../../api'
import {
  filterProviderOptionsByAuth,
  getAuthMatrixRevision,
  getAuthMatrixStatus,
  isChannelEffectiveAllowed,
  subscribeAuthMatrix,
} from './authMatrixRuntime'
import { invalidateSingleFlightCache } from './canvasRequestDedup'
import { loadAvailableProviderOptionsDeduped } from './canvasSharedConfigLoaders'
import { sortProviderOptionsCanonical } from './capabilityProviderSelection'
import { nextAutoRetryDelayMs } from './providerOptionsRetryPolicy'

/**
 * @param nodeType — Canvas node_type for auth-matrix channel filtering
 *   (VistoeraTASK-00634). When omitted, options are not auth-filtered.
 */
export function useCapabilityProviderOptions(
  workspaceId: string,
  capability: string,
  nodeType?: string,
) {
  const { i18n } = useTranslation()
  const locale = i18n.language
  const [rawOptions, setRawOptions] = useState<WorkspaceProviderOption[]>([])
  const [defaultProviderOptionId, setDefaultProviderOptionId] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [refreshTick, setRefreshTick] = useState(0)
  const autoRetryCountRef = useRef(0)
  useSyncExternalStore(subscribeAuthMatrix, getAuthMatrixRevision, getAuthMatrixRevision)
  const authStatus = getAuthMatrixStatus()

  useEffect(() => {
    if (!workspaceId || !capability) return
    const refetchNow = () => {
      invalidateSingleFlightCache(`workspaceProviderOptions:${workspaceId}:${capability}`)
      autoRetryCountRef.current = 0
      setRefreshTick((n) => n + 1)
    }
    window.addEventListener('focus', refetchNow)
    // Transient outages (deploy restarts, brief network loss) often
    // resolve exactly when connectivity comes back — retry immediately
    // instead of waiting for the next focus/blur cycle.
    window.addEventListener('online', refetchNow)
    return () => {
      window.removeEventListener('focus', refetchNow)
      window.removeEventListener('online', refetchNow)
    }
  }, [workspaceId, capability])

  useEffect(() => {
    if (!workspaceId || !capability) {
      setRawOptions([])
      setDefaultProviderOptionId(null)
      setError(false)
      setLoading(false)
      return
    }
    let cancelled = false
    let retryTimer: number | undefined
    setError(false)
    setLoading(true)
    loadAvailableProviderOptionsDeduped(workspaceId, capability, locale)
      .then((res) => {
        if (cancelled) return
        autoRetryCountRef.current = 0
        const raw = Array.isArray(res.options) ? res.options : []
        const sorted = sortProviderOptionsCanonical(raw)
        setRawOptions(sorted)
        const serverDefault =
          typeof res.default_provider_option_id === 'string' ? res.default_provider_option_id.trim() : ''
        setDefaultProviderOptionId(
          serverDefault || sorted.find((o) => o.is_default)?.provider_option_id || sorted[0]?.provider_option_id || null,
        )
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setRawOptions([])
        setDefaultProviderOptionId(null)
        setLoading(false)
        const attempt = autoRetryCountRef.current
        const delay = nextAutoRetryDelayMs(attempt)
        if (delay != null) {
          // Keep showing the loading/placeholder state during a bounded
          // auto-retry window rather than immediately surfacing the
          // user-facing "加载失败" for what may be a one-off blip.
          autoRetryCountRef.current = attempt + 1
          retryTimer = window.setTimeout(() => {
            if (cancelled) return
            invalidateSingleFlightCache(`workspaceProviderOptions:${workspaceId}:${capability}`)
            setRefreshTick((n) => n + 1)
          }, delay)
        } else {
          setError(true)
        }
      })
    return () => {
      cancelled = true
      if (retryTimer != null) window.clearTimeout(retryTimer)
    }
  }, [workspaceId, capability, locale, refreshTick])

  const options = useMemo(() => {
    if (!nodeType) return rawOptions
    return filterProviderOptionsByAuth(nodeType, rawOptions)
  }, [nodeType, rawOptions, authStatus])

  const defaultOption = useMemo(() => {
    // Prefer server default only when still allowed for this node.
    if (defaultProviderOptionId) {
      const hit = options.find((o) => o.provider_option_id === defaultProviderOptionId)
      if (hit) return hit
      if (nodeType && !isChannelEffectiveAllowed(nodeType, defaultProviderOptionId)) {
        // Lost permission — pick first remaining allowed option (never silent
        // switch during an in-flight generate; UI just has no valid default).
      }
    }
    return options.find((o) => o.is_default) ?? options[0] ?? null
  }, [defaultProviderOptionId, options, nodeType])

  const authLoading = Boolean(nodeType) && (authStatus === 'loading' || authStatus === 'idle')
  // TASK-00635: on auth ERROR keep options empty — never fall back to raw list.
  const authError = Boolean(nodeType) && authStatus === 'error'

  return {
    options,
    defaultOption,
    defaultProviderOptionId: defaultOption?.provider_option_id ?? null,
    error,
    loading: loading || authLoading,
    authError,
    noAllowedProviders:
      Boolean(nodeType) &&
      authStatus === 'ready' &&
      options.length === 0 &&
      rawOptions.length > 0,
  }
}
